import React, { useMemo, useState } from "react";
import {
	CustomDialog,
	CustomIconButton,
	CustomTooltip,
	icon,
	IconComponent,
} from "../../../../../../common/components";
import styles from "./ExecutionRow.module.css";
import {
	isNonEmptyObject,
	hourMinTimestamp,
	shortDateTimestamp,
	getTimeDifferenceTimestamps,
} from "../../../../../../common/utils";
import { formatExecutionCost, getExecutionCost, translateEnumExecutionStatus } from "../../utils";
import { EXECUTION_STATUS } from "../../constants";
import { translate } from "../../../../../../common/providers";
import LaunchDialog from "../models/LaunchDialog";
import { useApi } from "../../../../../../common/hooks";
import { AiAgentsService } from "../../../../../../api";

const formatLaunchDate = (timestamp) => shortDateTimestamp(timestamp);
const formatLaunchTime = (timestamp) =>
	new Date(timestamp).toLocaleTimeString([], {
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hour12: false,
	});
const formatPlaceholdersTooltip = (parameters) => {
	if (!isNonEmptyObject(parameters)) {
		return "";
	}
	return Object.entries(parameters)
		.map(([key, value]) => `${key}: ${value}`)
		.join("\n");
};
const formatPlaceholdersPreview = (parameters) => {
	if (!isNonEmptyObject(parameters)) {
		return "";
	}
	return Object.values(parameters).join(", ");
};
export default function ExecutionRow({
	isCollapsed = false,
	isHeader,
	execution,
	sortDirection,
	sortKey,
	onClickSort,
	onDelete,
	onDownloadTrace,
	onLaunch,
	onRetry,
	onToggleGroup,
}) {
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [openLaunchDialog, setOpenLaunchDialog] = useState(false);
	const [executedAgent, setExecutedAgent] = useState();
	const { call: getExecutedAgent } = useApi(AiAgentsService.getExecutedAgent);
	const {
		agentId,
		agentName,
		created,
		completed,
		costTotal,
		projectName,
		parameters,
		status,
		tokensUsed = 0,
		toolsCalled,
		results,
		projectId,
		id,
	} = useMemo(
		() =>
			(isHeader && {
				agentName: translate(execution[0].name),
				created: translate(execution[1].name),
				completed: translate(execution[2].name),
				costTotal: translate(execution[3].name),
				projectName: translate(execution[5].name),
				parameters: translate(execution[6].name),
				status: translate(execution[7].name),
				tokensUsed: translate(execution[8].name),
				toolsCalled: translate(execution[9].name),
				results: translate(execution[10].name),
			}) ||
			(isNonEmptyObject(execution) && execution),
		[execution, isHeader]
	);
	const isKeySorting = useMemo(() => sortKey === "name", [sortKey]);
	// eslint-disable-next-line no-underscore-dangle
	const isGroupedChild = !isHeader && execution?.__isGroupedChild;
	// eslint-disable-next-line no-underscore-dangle
	const hasGroupedChildren = !isHeader && execution?.__hasGroupedChildren;
	// eslint-disable-next-line no-underscore-dangle
	const groupedChildrenCount = execution?.__groupedChildrenCount || 0;
	const handleRetry = () => {
		if (onRetry && typeof onRetry === "function") {
			onRetry({ agentId, projectId: Number.parseInt(projectId, 10), parameters });
		}
	};
	const handleOpenDelete = () => {
		setOpenDeleteDialog((prev) => !prev);
	};
	const handleDelete = () => {
		if (typeof onDelete === "function") {
			onDelete(id);
		}
	};
	const handleDownload = () => {
		if (onDownloadTrace && typeof onDownloadTrace === "function") {
			onDownloadTrace(id);
		}
	};
	const handleOpenLaunchDialog = () => {
		setOpenLaunchDialog((prev) => !prev);
		getExecutedAgent({ executionId: id })
			.then((data) => setExecutedAgent(data))
			.catch(console.error);
	};
	const isDownloadEnabled = useMemo(
		() => status === EXECUTION_STATUS.COMPLETED_SUCCESS || status === EXECUTION_STATUS.COMPLETED_FAILURE,
		[status]
	);
	const placeholdersTooltip = useMemo(() => formatPlaceholdersTooltip(parameters), [parameters]);
	const placeholdersPreview = useMemo(() => formatPlaceholdersPreview(parameters), [parameters]);
	const projectTooltip = useMemo(() => (projectId != null ? `projectId: ${projectId}` : ""), [projectId]);
	const executionCost = useMemo(() => formatExecutionCost(getExecutionCost(execution)), [execution]);
	// eslint-disable-next-line no-underscore-dangle
	const totalExecutionCost = useMemo(() => formatExecutionCost(execution?.__totalGroupedCost), [execution]);
	const handleToggleGroup = () => {
		if (hasGroupedChildren && typeof onToggleGroup === "function") {
			onToggleGroup(id);
		}
	};
	return (
		<div className={`${styles.row} ${isGroupedChild ? styles.groupedChildRow : ""}`}>
			{(isHeader && (
				<div
					className={`${styles.sortLabel} ${styles.column} ${styles["col__width--160"]}`}
					role="presentation"
					onClick={() => onClickSort("name")}
				>
					{agentName}
					{!isKeySorting && (
						<IconComponent className={`${styles.icon} ${styles["icon--inactive"]}`} icon={icon.faSort} />
					)}
					{isKeySorting &&
						((sortDirection === "asc" && <IconComponent className={styles.icon} icon={icon.faSortUp} />) ||
							(sortDirection === "desc" && (
								<IconComponent className={styles.icon} icon={icon.faSortDown} />
							)))}
				</div>
			)) || (
				<div
					className={`${styles.cutText} ${styles["col__width--160"]} ${
						isGroupedChild ? styles.groupedChildName : styles.parentNameCell
					}`}
				>
					{hasGroupedChildren && (
						<button className={styles.groupToggleButton} type="button" onClick={handleToggleGroup}>
							<IconComponent
								className={`${styles.groupCaret} ${isCollapsed ? styles.groupCaretCollapsed : ""}`}
								icon={icon.faCaretDown}
							/>
							<span className={styles.groupCount}>{groupedChildrenCount}</span>
						</button>
					)}
					{isGroupedChild && <span className={styles.groupedChildMarker} />}
					<span>{agentName}</span>
				</div>
			)}
			<CustomTooltip title={!isHeader && created ? hourMinTimestamp(created) : ""}>
				<div className={`${styles.launchCell} ${styles["col__width--100"]}`}>
					{(isHeader && created) || (
						<>
							<span>{formatLaunchDate(created)}</span>
							<span className={styles.launchTime}>{formatLaunchTime(created)}</span>
						</>
					)}
				</div>
			</CustomTooltip>
			<div className={`${styles.cutText} ${styles["col__width--100"]}`}>
				{(isHeader && completed) || getTimeDifferenceTimestamps(created, completed)}
			</div>
			<div className={`${styles.cutText} ${styles["col__width--100"]}`}>
				{(isHeader && costTotal) || executionCost}
			</div>
			<div className={`${styles.cutText} ${styles["col__width--100"]}`}>
				{(isHeader && translate(execution[4].name)) ||
					(!isGroupedChild && hasGroupedChildren ? totalExecutionCost : "—")}
			</div>
			<CustomTooltip title={isHeader ? "" : projectTooltip}>
				<div className={`${styles.cutText} ${styles["col__width--160"]}`}>{projectName}</div>
			</CustomTooltip>
			<CustomTooltip title={isHeader ? "" : placeholdersTooltip}>
				<div className={`${styles.cutText} ${styles["col__width--200"]}`}>
					{(isHeader && parameters) || placeholdersPreview}
				</div>
			</CustomTooltip>
			<div className={`${styles.cutText} ${styles["col__width--100"]}`}>
				{(isHeader && status) || (
					<span className={styles.status} data-status={status}>
						{translateEnumExecutionStatus(status)}
					</span>
				)}
			</div>
			<div className={`${styles.cutText} ${styles["col__width--100"]}`}>
				{(isHeader && tokensUsed) || new Intl.NumberFormat("fr-FR").format(tokensUsed || 0)}
			</div>
			<div className={`${styles.cutText} ${styles["col__width--160"]}`}>{toolsCalled || 0}</div>
			<div className={`${styles.cutText} ${styles["col__width--200"]}`}>{results}</div>
			<div className={styles.actions}>
				{!isHeader && (
					<>
						<CustomIconButton
							icon={icon.faRotate}
							size="sm"
							tooltip={translate("common:btn.relaunch")}
							variant="outlined"
							onClick={handleRetry}
						/>
						<CustomIconButton
							icon={icon.faPen}
							size="sm"
							variant="outlined"
							onClick={handleOpenLaunchDialog}
						/>
						<CustomIconButton icon={icon.faTrash} size="sm" variant="outlined" onClick={handleOpenDelete} />
						<CustomIconButton
							disabled={!isDownloadEnabled}
							icon={icon.faDownload}
							size="sm"
							variant="outlined"
							onClick={handleDownload}
						/>
					</>
				)}
			</div>
			<CustomDialog
				iconColor="var(--red-500)"
				iconModel={icon.faExclamationTriangle}
				open={openDeleteDialog}
				subTitle={translate("dsi.agents.execution.delete.warning")}
				title={translate("common:delete-confirmation-popup.title")}
				onClose={handleOpenDelete}
				onSubmit={handleDelete}
			/>
			<LaunchDialog
				executedAgent={executedAgent}
				open={openLaunchDialog}
				onClose={handleOpenLaunchDialog}
				onLaunch={onLaunch}
			/>
		</div>
	);
}
